/**
 * E911 API (Phase 5, ADR 0027/0041): per-device posture, location
 * assignment (protected `e911.manage`; the location anchors the home node
 * and the callback DID the device presents), the 933 test workflow — a
 * LOCATION is verified by an ok on both the primary and backup paths, a
 * device by its location being verified plus an attestation that it is
 * there (ADR 0047) — plus the
 * node-side location sync (scan / push) and the emergency-call event log.
 */

import { apiFetch } from "./client";

/** One device's E911 posture. */
export interface E911Status {
  device_id: number;
  mac: string;
  label: string | null;
  e911_state: "unverified" | "verified";
  e911_verified_at: string | null;
  location_id: number | null;
  location_name: string | null;
  home_node_id: number | null;
  home_node_name: string | null;
  calls_prefer_home_node: boolean;
  did_id: number | null;
  did_number: string | null;
  blockers: string[];
  /** ADR 0047: the location's 933 progress (both required paths = verified). */
  location_verified: boolean;
  paths_passed: string[];
  paths_required: string[];
  /** ADR 0047: who vouched this phone is at its location since its last reset. */
  attested: boolean;
  attested_at: string | null;
  attested_by: string | null;
}

/** One device's node-side sync state (the scheduled `e911_sync` check item). */
export interface E911SyncItem {
  device_id: number;
  mac: string;
  subject: string;
  location_id: number;
  home_node: string | null;
  callback: string | null;
  ready: boolean;
  problems: string[];
  instruction: string | null;
}

/** The scan result: items + the derived per-extension plan. */
export interface E911SyncScan {
  items: E911SyncItem[];
  ready: boolean;
  gaps: number;
  applicable: boolean;
  notes: string[];
  plan: {
    node: string;
    domain: string;
    extension: string;
    location: string;
    callback: string | null;
    homed: boolean;
    device_ids: number[];
  }[];
}

/** What a push did, per Vodia row and per extension. */
export interface E911PushReport {
  entries: { node: string; domain: string; name: string; action: string }[];
  assignments: {
    node: string;
    domain: string;
    extension: string;
    location: string;
    action: string;
  }[];
  /** The tenant emergency webhook (action URL) per node/domain; empty when no receiver URL is set. */
  webhooks?: { node: string; domain: string; action: string }[];
  notes: string[];
  written: number;
}

/** One observed emergency call (911/933). */
export interface EmergencyCallEventRow {
  id: number;
  observed_at: string | null;
  source: "cdr" | "webhook";
  node: string | null;
  domain: string | null;
  extension: string | null;
  dialed: string | null;
  device_id: number | null;
  home_node: string | null;
  egress_ok: boolean | null;
  connected: boolean | null;
  duration_seconds: number | null;
  detail: string | null;
  notified_at: string | null;
  notify_error: string | null;
}

/** A recorded 933 test. */
export interface E911TestRow {
  id: number;
  path: string;
  result: string;
  actor: string;
  notes: string | null;
  tested_at: string | null;
}

/** Picker options for assignment. */
export interface E911Options {
  locations: {
    id: number;
    name: string;
    location_type: string;
    has_dispatchable_address: boolean;
    home_node_name: string | null;
    callback_number: string | null;
  }[];
}

export function getE911Devices(): Promise<{ items: E911Status[] }> {
  return apiFetch("/api/e911/devices");
}

export function getE911Detail(
  deviceId: number,
): Promise<E911Status & { history: E911TestRow[] }> {
  return apiFetch(`/api/e911/devices/${deviceId}`);
}

export function getE911Options(): Promise<E911Options> {
  return apiFetch("/api/e911/options");
}

export function assignE911Location(
  deviceId: number,
  locationId: number | null,
): Promise<E911Status> {
  return apiFetch(`/api/e911/devices/${deviceId}/location`, {
    method: "POST",
    body: { location_id: locationId },
  });
}

export function recordE911Test(
  deviceId: number,
  body: { path: string; result: string; notes?: string },
): Promise<E911Status> {
  return apiFetch(`/api/e911/devices/${deviceId}/verifications`, {
    method: "POST",
    body,
  });
}

/** Attest the device is at its assigned location (protected `e911.manage`; ADR 0047). */
export function attestE911Device(deviceId: number, notes?: string): Promise<E911Status> {
  return apiFetch(`/api/e911/devices/${deviceId}/attest`, {
    method: "POST",
    body: JSON.stringify({ notes: notes ?? null }),
  });
}

/** Live comparison of the platform's location intent with the nodes. */
export function getE911Sync(): Promise<E911SyncScan> {
  return apiFetch("/api/e911/sync");
}

/** Push the location model to every node (captured write shapes; audited). */
export function pushE911Sync(): Promise<E911PushReport> {
  return apiFetch("/api/e911/sync/push", { method: "POST" });
}

/** Observed emergency calls, newest first. */
export function getEmergencyCallEvents(): Promise<{
  items: EmergencyCallEventRow[];
}> {
  return apiFetch("/api/e911/events");
}
